<template>
  <div class="betrieb-view">
    <div v-if="locationReady && locationID !== 'NB'" class="card">
      <div class="card-body">
        <el-alert
          title="Diese Ansicht ist vorerst nur für den Standort Neroberg verfügbar."
          type="warning"
          :closable="false"
          show-icon
        ></el-alert>
      </div>
    </div>
    <template v-else-if="locationReady">
      <!-- Header -->
      <div class="card header-card">
        <div class="card-body d-flex flex-column flex-md-row justify-content-between align-items-md-center">
          <div>
            <h4 class="card-title mb-1">
              <i class="bi bi-sunset"></i> Betriebsende
            </h4>
            <p class="text-muted mb-0">{{ todayLabel }}</p>
          </div>
          <div class="save-indicator mt-2 mt-md-0">
            <span v-if="saving" class="text-warning">
              <i class="el-icon-loading"></i> Speichert&hellip;
            </span>
            <span v-else-if="lastSavedAt" class="text-success">
              <i class="bi bi-check-circle-fill"></i> Zwischengespeichert {{ lastSavedLabel }}
            </span>
            <span v-else class="text-muted">
              <i class="bi bi-cloud"></i> Bereit
            </span>
          </div>
        </div>
      </div>

      <!-- Schließzeit & Wetter -->
      <div class="card section-card" v-loading="loading">
        <div class="card-header">
          <h5 class="card-title mb-0"><i class="bi bi-clock-history"></i> Zeiten & Wetter</h5>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-12 col-md-6 mb-3 mb-md-0">
              <label class="small text-muted mb-1">Schließzeit</label>
              <el-time-select
                v-model="close"
                value-format="HH:mm"
                :picker-options="{ start: '13:00', step: '00:15', end: '22:00' }"
                placeholder="Schließzeit wählen"
                class="w-100"
              ></el-time-select>
            </div>
            <div class="col-12 col-md-6">
              <label class="small text-muted mb-1">Wetter</label>
              <el-select v-model="wetter" placeholder="Bitte wählen" class="w-100">
                <el-option label="sonnig" value="sonnig"></el-option>
                <el-option label="Regen" value="Regen"></el-option>
                <el-option label="wechselhaft" value="wechselhaft"></el-option>
                <el-option label="Wind" value="Wind"></el-option>
                <el-option label="Sturm/Gewitter" value="Sturm/Gewitter"></el-option>
              </el-select>
            </div>
          </div>
        </div>
      </div>

      <!-- Trainer -->
      <div class="card section-card" v-loading="loading">
        <div class="card-header">
          <h5 class="card-title mb-0"><i class="bi bi-people"></i> Mitarbeiter</h5>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-12 col-md-6 mb-3 mb-md-0">
              <label class="small text-muted mb-1">Tagesverantwortlicher</label>
              <el-select
                v-model="HT"
                placeholder="Tagesverantwortlichen wählen"
                filterable
                class="w-100"
              >
                <el-option
                  v-for="t in trainer"
                  :key="t.trainerName"
                  :value="t.trainerName"
                  :label="t.trainerName"
                ></el-option>
              </el-select>
            </div>
            <div class="col-12 col-md-6">
              <label class="small text-muted mb-1">Weitere Mitarbeiter</label>
              <el-select
                v-model="allTrainer"
                placeholder="Mitarbeiter wählen"
                filterable
                multiple
                class="w-100"
              >
                <el-option
                  v-for="t in trainer"
                  :key="t.trainerName"
                  :value="t.trainerName"
                  :label="t.trainerName"
                ></el-option>
              </el-select>
            </div>
          </div>
        </div>
      </div>

      <!-- HT-Checkliste -->
      <div class="card section-card" v-loading="loading">
        <div class="card-header">
          <h5 class="card-title mb-0">
            <i class="bi bi-list-check"></i> Checkliste HT Betriebsende
          </h5>
        </div>
        <div class="card-body">
          <p v-if="!checklistItems.length && !loading" class="text-muted mb-0">
            Keine Checklistenpunkte gefunden.
          </p>
          <div
            v-for="item in checklistItems"
            :key="item.key"
            class="checklist-row"
            :class="{ 'checklist-row--prev-missed': item.prevUnchecked }"
          >
            <p-checkbox
              v-model="item.checked"
              class="checklist-row__check"
            >{{ item.checkItem }}</p-checkbox>
            <button
              v-if="!item.noteOpen && !item.note"
              type="button"
              class="checklist-row__note-toggle"
              title="Anmerkung hinzufügen"
              aria-label="Anmerkung hinzufügen"
              @click="toggleNote(item)"
            >
              <i class="bi bi-chat-left-text"></i>
            </button>
            <div
              class="checklist-row__input"
              v-if="item.fieldType >= 2"
            >
              <el-input
                v-if="item.fieldType === 2"
                type="number"
                min="0"
                size="small"
                v-model="item.inputValue"
                placeholder="0"
                :disabled="!item.checked"
              ></el-input>
              <el-input
                v-else-if="item.fieldType === 4"
                type="text"
                size="small"
                v-model="item.inputValue"
                placeholder="Bitte eingeben"
                :disabled="!item.checked"
              ></el-input>
              <el-select
                v-else-if="item.fieldType === 3"
                v-model="item.inputValue"
                filterable
                size="small"
                placeholder="Mitarbeiter wählen"
                class="w-100"
                :disabled="!item.checked"
              >
                <el-option
                  v-for="t in trainer"
                  :key="t.trainerName"
                  :value="t.trainerName"
                  :label="t.trainerName"
                ></el-option>
              </el-select>
            </div>
            <div v-if="item.prevUnchecked" class="checklist-row__alert">
              <i class="bi bi-exclamation-triangle-fill"></i>
              Am {{ item.prevDate }} nicht erledigt
            </div>
            <div v-if="item.noteOpen || item.note" class="checklist-row__note-editor">
              <div class="checklist-row__note-header">
                <span class="small text-muted">
                  <i class="bi bi-chat-left-text"></i> Anmerkung
                </span>
                <span
                  v-if="item.prevNoteDate && item.note && item.note === item.prevNoteText"
                  class="small text-warning ml-2"
                >
                  (übernommen vom {{ item.prevNoteDate }})
                </span>
              </div>
              <el-input
                type="textarea"
                :autosize="{ minRows: 2, maxRows: 6 }"
                size="small"
                v-model="item.note"
                placeholder="Anmerkung eingeben..."
                @input="onNoteInput(item)"
              ></el-input>
              <button
                v-if="item.note"
                type="button"
                class="btn btn-link btn-sm text-danger checklist-row__note-clear"
                @click="clearNote(item)"
              >
                <i class="bi bi-x-circle"></i> Anmerkung entfernen
              </button>
            </div>
          </div>
          <div class="row align-items-center mt-3 pt-2 border-top">
            <label class="col-sm-4 col-form-label">Ausgefüllt von</label>
            <div class="col-sm-8">
              <el-select
                v-model="processedBy"
                placeholder="Trainer wählen"
                filterable
                class="w-100"
              >
                <el-option
                  v-for="t in trainer"
                  :key="t.trainerName"
                  :value="t.trainerName"
                  :label="t.trainerName"
                ></el-option>
              </el-select>
            </div>
          </div>
        </div>
      </div>

      <!-- Anmerkung zum Tag -->
      <div class="card section-card" v-loading="loading">
        <div class="card-header">
          <h5 class="card-title mb-0">
            <i class="bi bi-journal-text"></i> Anmerkungen zum Tag
          </h5>
        </div>
        <div class="card-body">
          <textarea
            class="form-control px-2"
            placeholder="Anmerkungen, besondere Vorkommnisse, Unfälle, Beinaheunfälle..."
            rows="5"
            v-model="anmerkungText"
          ></textarea>
          <p class="small text-muted mt-2 mb-0">
            <i class="bi bi-info-circle"></i>
            Wird als Anmerkung des Tages gespeichert und erscheint im Tagesreport.
          </p>
        </div>
      </div>

      <!-- Feedback an Trainer -->
      <div class="card section-card" v-loading="loading">
        <div class="card-header">
          <h5 class="card-title mb-0">
            <i class="bi bi-chat-left-text"></i> Feedback an Mitarbeiter senden
          </h5>
        </div>
        <div class="card-body">
          <p-switch v-model="sendTrainerFeedback" type="primary">
            <i class="fa fa-check" slot="on"></i>
            <i class="fa fa-times" slot="off"></i>
          </p-switch>
          <span class="ml-2">Feedback an Mitarbeiter senden (optional)</span>
          <div v-if="sendTrainerFeedback" class="mt-3">
            <textarea
              class="form-control px-2"
              placeholder="Feedback an die Mitarbeiter..."
              rows="6"
              v-model="trainerFeedback"
            ></textarea>
            <p class="small text-muted mt-2 mb-0">
              <i class="bi bi-info-circle"></i>
              Das Feedback wird gegen 21 Uhr automatisch per E-Mail an alle Mitarbeiter gesendet,
              die heute im Einsatz waren.
            </p>
          </div>
        </div>
      </div>

      <div class="card section-card">
        <div class="card-body">
          <p class="small text-muted mb-0">
            <i class="bi bi-info-circle"></i>
            Eingaben werden automatisch gespeichert. Die Ansicht kann während des aktuellen
            Tages jederzeit erneut geöffnet und angepasst werden.
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from 'vue'
import { db, Timestamp } from '../../../../firebaseDb'
import { Input, Select, Option, TimeSelect, Alert, Loading } from 'element-ui'
import moment from 'moment-timezone'
import store from '../../../../store'
import PSwitch from 'src/components/UIComponents/Switch.vue'

Vue.use(TimeSelect)
Vue.use(Loading.directive)

export default {
  components: {
    [Input.name]: Input,
    [Select.name]: Select,
    [Option.name]: Option,
    [TimeSelect.name]: TimeSelect,
    [Alert.name]: Alert,
    PSwitch
  },
  data() {
    return {
      loading: true,
      dataLoaded: false,
      saving: false,
      lastSavedAt: null,
      nowTick: 0,
      nowTimer: null,
      saveTimer: null,
      reportName: '',
      anmerkungDocId: '',
      todayLabel: '',
      close: '',
      wetter: '',
      HT: '',
      allTrainer: [],
      anmerkungText: '',
      anmerkungImportant: false,
      sendTrainerFeedback: false,
      trainerFeedback: '',
      checklistItems: [],
      processedBy: '',
      trainer: [],
      unwatchLocation: null
    }
  },
  computed: {
    locationID() {
      return store.getters.userlocation
    },
    locationReady() {
      const l = this.locationID
      return l !== 0 && l !== null && l !== undefined
    },
    lastSavedLabel() {
      // eslint-disable-next-line no-unused-vars
      const _ = this.nowTick
      if (!this.lastSavedAt) return ''
      const secs = Math.max(0, Math.round((Date.now() - this.lastSavedAt.getTime()) / 1000))
      if (secs < 5) return 'gerade eben'
      if (secs < 60) return 'vor ' + secs + ' Sekunden'
      const mins = Math.floor(secs / 60)
      if (mins === 1) return 'vor 1 Minute'
      if (mins < 60) return 'vor ' + mins + ' Minuten'
      const hours = Math.floor(mins / 60)
      if (hours === 1) return 'vor 1 Stunde'
      return 'vor ' + hours + ' Stunden'
    }
  },
  created() {
    moment.locale('de')
    this.todayLabel = moment().format('dddd, DD. MMMM YYYY')
    this.nowTimer = setInterval(() => {
      this.nowTick++
    }, 15000)
    if (this.locationReady) {
      this.initForLocation()
    } else {
      this.unwatchLocation = store.watch(
        (state) => state.userlocation,
        () => {
          if (this.locationReady) {
            if (this.unwatchLocation) {
              this.unwatchLocation()
              this.unwatchLocation = null
            }
            this.initForLocation()
          }
        }
      )
    }
  },
  beforeDestroy() {
    if (this.nowTimer) {
      clearInterval(this.nowTimer)
      this.nowTimer = null
    }
    if (this.unwatchLocation) {
      this.unwatchLocation()
      this.unwatchLocation = null
    }
    if (this.saveTimer) {
      clearTimeout(this.saveTimer)
      this.saveTimer = null
      if (this.dataLoaded) {
        this.persist()
      }
    }
  },
  methods: {
    initForLocation() {
      if (this.locationID !== 'NB') {
        this.loading = false
        return
      }
      this.reportName = moment().format('YYYYMMDD') + '-NB'
      this.anmerkungDocId = this.reportName + '-betriebsende-tag'
      this.loadAll()
    },
    async loadTrainer() {
      const snap = await db
        .collection('trainer')
        .where('locationID', 'array-contains', 'NB')
        .where('isToday', '==', true)
        .where('isTodayLocation', '==', 'NB')
        .orderBy('Name')
        .get()
      this.trainer = []
      snap.forEach((doc) => {
        this.trainer.push({ trainerName: doc.data().Name })
      })
    },
    async loadAll() {
      this.loading = true
      try {
        await this.loadTrainer()

        // checklist items for HTEnde
        const itemsSnap = await db
          .collection('checklists/HTEnde/Items')
          .where('locationID', '==', 'NB')
          .orderBy('position')
          .get()
        const items = []
        const today = moment().isoWeekday()
        itemsSnap.forEach((doc) => {
          const storedWeekdays = doc.data().weekdays
          const weekdays = Array.isArray(storedWeekdays) && storedWeekdays.length > 0
            ? storedWeekdays.map((d) => Number(d))
            : [1, 2, 3, 4, 5, 6, 7]
          if (!weekdays.includes(today)) {
            return
          }
          items.push({
            key: doc.id,
            checkItem: doc.data().Item,
            fieldType: Number(doc.data().fieldType) || 1,
            position: doc.data().position,
            checked: false,
            inputValue: '',
            note: '',
            noteCleared: false,
            noteOpen: false,
            prevUnchecked: false,
            prevDate: '',
            prevNoteText: '',
            prevNoteDate: ''
          })
        })

        // existing HTEnde subcollection
        const htSnap = await db
          .collection('tagesreports/' + this.reportName + '/HTEnde')
          .get()
        const htByPos = {}
        let storedProcessedBy = ''
        htSnap.forEach((doc) => {
          if (doc.id === 'trainer') {
            storedProcessedBy = doc.data().processedBy || ''
          } else {
            const d = doc.data()
            htByPos[d.position] = d
          }
        })
        items.forEach((it) => {
          const existing = htByPos[it.position]
          if (existing) {
            it.checked = !!existing.checked
            it.inputValue = existing.value != null ? existing.value : ''
            it.note = existing.note != null ? existing.note : ''
            it.noteCleared = !!existing.noteCleared
          }
        })

        // carry-over: previous days' state and notes for highlight + carry-over
        const prior = await this.loadPriorChecklistState('HTEnde', 14)
        items.forEach((it) => {
          const existing = htByPos[it.position]
          const pp = prior[Number(it.position)]
          if (!pp) return
          if (pp.firstFound && !pp.firstChecked) {
            it.prevUnchecked = true
            it.prevDate = pp.firstDate
          }
          if (pp.latestNote) {
            it.prevNoteText = pp.latestNote
            it.prevNoteDate = pp.latestNoteDate
            // Only carry over note if there's no existing note document for today
            if (!existing && !it.note) {
              it.note = pp.latestNote
            }
          }
          if (it.note) {
            it.noteOpen = true
          }
        })

        this.checklistItems = items
        this.processedBy = storedProcessedBy

        // existing tagesreport doc
        const reportDoc = await db.collection('tagesreports').doc(this.reportName).get()
        let allTrainerLoaded = []
        if (reportDoc.exists) {
          const d = reportDoc.data()
          if (d.Close) this.close = d.Close
          if (d.Wetter) this.wetter = d.Wetter
          if (d.HT) this.HT = d.HT
          if (d.Trainer) {
            allTrainerLoaded = String(d.Trainer)
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
          }
          if (d.TrainerFeedback && d.TrainerFeedback.length > 0) {
            this.trainerFeedback = d.TrainerFeedback
            this.sendTrainerFeedback = true
          }
        }

        // anmerkung doc
        const anmDoc = await db.collection('anmerkungen').doc(this.anmerkungDocId).get()
        if (anmDoc.exists) {
          this.anmerkungText = anmDoc.data().text || ''
          this.anmerkungImportant = !!anmDoc.data().important
        }

        // default allTrainer: prefer stored; else today's trainers
        if (allTrainerLoaded.length > 0) {
          this.allTrainer = allTrainerLoaded
        } else if (this.trainer.length > 0) {
          this.allTrainer = this.trainer.map((t) => t.trainerName)
        }
      } catch (err) {
        console.error('Betriebsende loadAll error', err)
        this.$notify({
          message: 'Daten konnten nicht geladen werden: ' + err.message,
          type: 'danger',
          horizontalAlign: 'right',
          verticalAlign: 'bottom'
        })
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.dataLoaded = true
        })
      }
    },
    async loadPriorChecklistState(subcollection, maxDays) {
      // Returns { [position]: { firstFound, firstChecked, firstDate, latestNote, latestNoteDate } }
      // Uses asc order to match the existing (locationID asc, Date asc) composite index;
      // we sort to most-recent-first afterwards.
      try {
        const startOfToday = moment().startOf('day').toDate()
        const cutoff = moment().subtract(maxDays, 'days').startOf('day').toDate()
        const reportSnap = await db
          .collection('tagesreports')
          .where('locationID', '==', 'NB')
          .where('Date', '>=', Timestamp.fromDate(cutoff))
          .where('Date', '<', Timestamp.fromDate(startOfToday))
          .orderBy('Date', 'asc')
          .get()
        const reports = []
        reportSnap.forEach((d) => {
          const data = d.data()
          if (data && data.Date && typeof data.Date.toDate === 'function') {
            reports.push({ id: d.id, date: data.Date.toDate() })
          }
        })
        if (!reports.length) return {}
        // Most recent first
        reports.sort((a, b) => b.date.getTime() - a.date.getTime())
        const subs = await Promise.all(
          reports.map((r) =>
            db.collection('tagesreports/' + r.id + '/' + subcollection).get()
          )
        )
        const byPosition = {}
        reports.forEach((rep, idx) => {
          const dateStr = moment(rep.date).format('DD.MM.')
          subs[idx].forEach((doc) => {
            if (doc.id === 'trainer') return
            const data = doc.data() || {}
            if (data.position === undefined || data.position === null) return
            const pos = Number(data.position)
            if (Number.isNaN(pos)) return
            if (!byPosition[pos]) {
              byPosition[pos] = {
                firstFound: true,
                firstChecked: !!data.checked,
                firstDate: dateStr,
                latestNote: data.note ? String(data.note) : '',
                latestNoteDate: data.note ? dateStr : '',
                noteStateFound: !!data.note || !!data.noteCleared
              }
            } else if (!byPosition[pos].noteStateFound && (data.note || data.noteCleared)) {
              byPosition[pos].latestNote = String(data.note)
              byPosition[pos].latestNoteDate = data.note ? dateStr : ''
              byPosition[pos].noteStateFound = true
            }
          })
        })
        return byPosition
      } catch (err) {
        console.error('loadPriorChecklistState failed', err)
        return {}
      }
    },
    toggleNote(item) {
      item.noteOpen = !item.noteOpen
    },
    clearNote(item) {
      item.note = ''
      item.noteCleared = true
      item.noteOpen = false
    },
    onNoteInput(item) {
      item.noteCleared = !item.note
    },
    scheduleSave() {
      if (!this.dataLoaded) return
      if (this.saveTimer) clearTimeout(this.saveTimer)
      this.saving = true
      this.saveTimer = setTimeout(() => {
        this.saveTimer = null
        this.persist()
      }, 800)
    },
    async persist() {
      if (!this.dataLoaded) {
        this.saving = false
        return
      }
      try {
        const reportRef = db.collection('tagesreports').doc(this.reportName)
        const batch = db.batch()
        const payload = {
          Date: Timestamp.fromDate(new Date()),
          locationID: 'NB',
          Close: this.close || '',
          Wetter: this.wetter || '',
          HT: this.HT || '',
          Trainer: this.allTrainer.join(', '),
          TrainerFeedback:
            this.sendTrainerFeedback && this.trainerFeedback ? this.trainerFeedback : ''
        }
        batch.set(reportRef, payload, { merge: true })

        const htCol = reportRef.collection('HTEnde')
        this.checklistItems.forEach((it) => {
          batch.set(htCol.doc('item-' + it.position), {
            Task: it.checkItem,
            checked: !!it.checked,
            value: it.inputValue || '',
            note: it.note || '',
            noteCleared: !it.note && !!it.noteCleared,
            position: it.position
          })
        })
        batch.set(htCol.doc('trainer'), {
          processedBy: this.processedBy || '',
          position: 9999
        })

        await batch.commit()

        // anmerkung: upsert if non-empty, delete otherwise
        const anmRef = db.collection('anmerkungen').doc(this.anmerkungDocId)
        if (this.anmerkungText && this.anmerkungText.trim().length > 0) {
          await anmRef.set(
            {
              text: this.anmerkungText,
              important: !!this.anmerkungImportant,
              timestamp: Timestamp.fromDate(new Date()),
              locationID: 'NB',
              source: 'betriebsende'
            },
            { merge: true }
          )
        } else {
          // remove existing if present
          const existing = await anmRef.get()
          if (existing.exists) {
            await anmRef.delete()
          }
        }

        this.lastSavedAt = new Date()
        this.nowTick++
      } catch (err) {
        console.error('Betriebsende persist error', err)
        this.$notify({
          message: 'Zwischenspeichern fehlgeschlagen: ' + err.message,
          type: 'danger',
          horizontalAlign: 'right',
          verticalAlign: 'bottom'
        })
      } finally {
        this.saving = false
      }
    }
  },
  watch: {
    close() { this.scheduleSave() },
    wetter() { this.scheduleSave() },
    HT() { this.scheduleSave() },
    allTrainer() { this.scheduleSave() },
    anmerkungText() { this.scheduleSave() },
    sendTrainerFeedback() { this.scheduleSave() },
    trainerFeedback() { this.scheduleSave() },
    processedBy() { this.scheduleSave() },
    checklistItems: { deep: true, handler() { this.scheduleSave() } }
  }
}
</script>

<style lang="scss" scoped>
.betrieb-view {
  max-width: 960px;
  margin: 0 auto;
  padding: 0 4px;
}
.betrieb-view .card {
  margin-bottom: 1.75rem;
}
.header-card .card-title {
  color: #2c3e50;
}
.header-card .card-body {
  padding-top: 0rem;
  padding-bottom: 1rem;
}
.section-card {
  margin-bottom: 1.75rem;
}
.section-card .card-header {
  background: transparent;
  border-bottom: 1px solid #eee;
  padding: 0.85rem 1.25rem;
}
.checklist-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 16px;
  row-gap: 10px;
  padding: 0.75rem 12px;
  border-bottom: 1px dashed #f0f0f0;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}
.checklist-row:last-child {
  border-bottom: none;
}
.checklist-row--prev-missed {
  background-color: rgba(255, 193, 7, 0.10);
  box-shadow: inset 3px 0 0 #f0a300;
}
.checklist-row__check {
  flex: 1 1 260px;
  min-width: 0;
  margin: 0 !important;
}
.checklist-row__input {
  flex: 0 0 240px;
  min-width: 200px;
}
.checklist-row__alert {
  flex: 1 1 100%;
  font-size: 0.85rem;
  color: #b76e00;
  display: flex;
  align-items: center;
  gap: 6px;
}
.checklist-row__note-toggle {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 50%;
  background: transparent;
  color: #9aa0a6;
  font-size: 1rem;
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
}
.checklist-row__note-toggle:hover,
.checklist-row__note-toggle:focus {
  color: #15A34A;
  background-color: rgba(21, 163, 74, 0.08);
  border-color: rgba(21, 163, 74, 0.25);
  outline: none;
}
.checklist-row__note-editor {
  flex: 1 1 100%;
  background: #fafafa;
  border: 1px solid #ececec;
  border-radius: 6px;
  padding: 10px 12px;
  margin-top: 4px;
}
.checklist-row__note-header {
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  i {
    margin-right: 4px;
  }
}
.checklist-row__note-clear {
  padding-left: 0;
  padding-right: 0;
  font-size: 0.8rem;
  margin-top: 4px;
  text-decoration: none;
  i {
    margin-right: 4px;
  }
}
@media (max-width: 575.98px) {
  .checklist-row__input {
    flex: 1 1 100%;
  }
}
/* ensure multi-select tags container in this view keeps readable left padding */
.betrieb-view ::v-deep .el-select .el-select__tags {
  padding-left: 8px;
}
.betrieb-view ::v-deep .el-textarea__inner {
  padding-left: 15px;
}
/* use app green for multi-select tags */
.betrieb-view ::v-deep .el-select .el-tag,
.betrieb-view ::v-deep .el-select .el-tag.el-tag--info,
.betrieb-view ::v-deep .el-select .el-tag.el-tag--primary,
.betrieb-view ::v-deep .el-select .el-tag.el-tag--light {
  background-color: rgba(21, 163, 74, 0.12) !important;
  border-color: rgba(21, 163, 74, 0.35) !important;
  color: #15A34A !important;
  margin-right: 3px !important;
  padding: 0 6px !important;
}
.betrieb-view ::v-deep .el-select .el-tag .el-tag__close {
  background-color: #15A34A !important;
  color: #fff !important;
}
.betrieb-view ::v-deep .el-select .el-tag .el-tag__close:hover {
  background-color: rgb(19, 147, 67) !important;
}
.save-indicator {
  font-size: 0.9rem;
  font-weight: 500;
  i {
    margin-right: 4px;
  }
}
textarea.form-control {
  resize: vertical;
}
</style>
